import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/app-storage/store.ts'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { useQuery } from '@tanstack/react-query'
import { FamilyApplicationQueries } from '@/queries/family_applications/queries.ts'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import {
  selectApplicationRequestStatus,
  UpdateFamilyAttestation,
} from '@/app-storage/applications/familyApplicationSlice.ts'
import { ATTESTATION_STATUS } from '@/app/pages/family_application/utils.ts'
import { AcctAttestationTextResponse } from '@/queries/family_applications/requests.ts'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { attestationRoute } from '@/routing/family_application_routes.tsx'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { useTranslation } from 'react-i18next'
import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '../constants'
import { useTypesafeStudent } from '../student_details/useTypesafeStudent'
import sanitizedHtml from 'sanitize-html'
import { cn } from '@/app/common/utilities'

export default function AttestationView() {
  const { t: translate } = useTranslation()
  const ProgramId = useSelector(selectProgramID)
  const { student } = useTypesafeStudent()
  const { attestation_id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const navigateTo = useNavigate()
  const { fireAlert } = useAppAlert()
  const ApplicationStatus = useSelector(selectApplicationRequestStatus)
  const attestationTextQuery = useQuery(
    FamilyApplicationQueries.fetchAcctAttestationsText(ProgramId, {
      staleTime: Infinity,
    })
  )
  const FamilyApplicationState = useSelector(
    (state: RootState) => state.FamilyApplication
  )
  // const ProgramSettings = useSelector(
  //   (state: RootState) => state.ProgramSettings
  // )

  interface AttestationLog {
    [key: string]: string
  }
  const logAttestationStepViewed = (
    currentAttestation: AcctAttestationTextResponse
  ) => {
    const dicAttestationLog: AttestationLog = {
      'level of disability': 'Level of disability',
      'financial responsibility': 'Financial Responsibility',
      'consent to services': 'Consent to Services',
      'return to a public school': 'Return to Public School',
      'qualifying provider I select': 'Qualifying Provider',
      'income my household': 'Household income',
    }

    for (const key in dicAttestationLog) {
      if (currentAttestation.attestation_text[0].includes(key)) {
        LogUserActivity({
          log_type: LOG_TYPES.VIEW_PAGE,
          suffix_text: `${dicAttestationLog[key]} attestation.`,
        })
      }
    }
  }

  async function handleAttestationConfirmation(
    currentAttestation: AcctAttestationTextResponse
  ) {
    logAttestationStepViewed(currentAttestation)
    const nextAttestation = FamilyApplicationState.data?.attestations
      .filter((attestation) => attestation.student_id == student.id)
      .find((attestation) => attestation.order == currentAttestation.order + 1)

    let nextRoute
    // If undefined, we've reached the end of the order sequence, or db records might have non-sequential order numbers
    if (nextAttestation == undefined) {
      nextRoute =
        AppRoutePaths.family_application.student_details.success_verification
    } else {
      nextRoute = attestationRoute(nextAttestation.id.toString())
    }

    const actionResult = await dispatch(
      UpdateFamilyAttestation({
        family_account_id: FamilyApplicationState.data!.family_account_id,
        attestation: {
          id: currentAttestation.id,
          status: ATTESTATION_STATUS.CONFIRMED,
        },
      })
    )

    if (UpdateFamilyAttestation.fulfilled.match(actionResult)) {
      navigateTo(nextRoute)
    } else {
      fireAlert({
        variant: 'error',
        message: actionResult.payload?.message ?? '',
      })
    }
  }

  if (attestationTextQuery.isError) {
    return <div>{attestationTextQuery.error.message}</div>
  }
  if (attestationTextQuery.isPending) return <></>

  const currentAttestation = attestationTextQuery.data.find(
    (attestationText) =>
      attestationText.id ==
      (attestation_id == undefined ? 0 : parseInt(attestation_id))
  )

  if (
    currentAttestation == undefined ||
    currentAttestation.attestation_text.length < 2
  ) {
    return <div>No attestation found</div>
  }



  const sanitizeText = (text: string) => {
    const defaultOptions = {
      allowedAttributes: {
        a: ['href', 'class', 'target'],
      },
    }

    return (
      <p
        className="mb-1 text-gray-700 text-sm"
        dangerouslySetInnerHTML={{
          __html: sanitizedHtml(text, defaultOptions),
        }}
      ></p>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1 className="text-xl font-medium text-gray-900 mb-5">
          {translate(currentAttestation.code ?? 'almost_done')}
        </h1>
        <p className="text-sm text-gray-600">
          {translate('complete_your_application')}:
        </p>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <section
          aria-labelledby="attestation-title"
          className={cn(
            'my-10',
            currentAttestation.code === 'local_education_agency_participation'
              ? 'pt-20 sm:pt-10'
              : ''
          )}
        >
          <h2 id="attestation-title" className="sr-only">
            Attestation Details
          </h2>
          <div className="mb-3">
            <h3 className="font-medium">{translate('i_acknowledge_that')}:</h3>

            {sanitizeText(currentAttestation.attestation_text[0])}
          </div>

          <div className="bg-primary-50 mb-4 rounded-md p-2">
            <h3 className="font-medium">{translate('what_does_this_mean')}</h3>

            {sanitizeText(currentAttestation.attestation_text[1])}
          </div>
        </section>

        <p
          className="text-center text-sm text-gray-400 mb-5"
          aria-live="polite"
        >
          {translate('attestation_progress', {
            current: currentAttestation.order,
            total: FamilyApplicationState.data?.attestations.filter(
              (attestation) => attestation.student_id == student.id
            ).length,
          })}
        </p>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          onClick={() => handleAttestationConfirmation(currentAttestation)}
          status={ApplicationStatus}
          buttonTxt={translate('agree')}
          aria-label={translate('agree_to_attestation')}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
